import React from "react";
import clsx from "clsx";

import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useDebouncedCallback } from "use-debounce";
import { useDispatch, useSelector } from "../../hooks";

import AlertContainer from "../../components/common/AlertContainer";

import { registerBusiness } from "./slices/register.actions";
import {
	onAlertClose,
	handleFieldValueChange,
	resetLookupData,
	resetEmailLookup,
} from "./slices/register.slice";
import { AutocompleteFix } from "../../components/common/AutocompleteFix";
import { STORES_ID } from "../../utils/stores";

import { SubmitButton } from "../../components/controls/SubmitButton";
import { trackEvent } from "../../utils/tracking";
import { TRACKING_EVENTS } from "../../utils/trackingDefinitions";
import SpinningCircle from "../../components/common/SpinningCircle";
import { ClubBenefits } from "../../components/common/ClubBenefits";
import {
	RegisterBusinessFields,
	RegisterBusinessPayload,
	RegisterUserFormFields,
} from "./slices/register.types";
import { CompanySearch } from "./components/CompanySearch";
import { CompanyAddress } from "./components/CompanyAddress";
import { CompanyConsents } from "./components/CompanyConsents";
import { CompanyContact } from "./components/CompanyContact";
import { createRegistrationFormSchema } from "./slices/register.schema";

import { svgIcon } from "../../utils/svgProvider";
import { normalizeValue } from "../../utils/helpers";
import { TYPE_DEBOUNCE } from "../settings/common.utils";
import Alert from "../../components/common/Alert";

export const RegistrationBusinessForm = () => {
	const dispatch = useDispatch();
	const { formatMessage } = useIntl();

	const organisationNumber = useSelector(
		(state) => state.registration.b2b.form.organisationNumber,
	);

	const subunitOrganisationNumberValidated = useSelector(
		(state) => state.registration.b2b.subunitOrganisationNumberValidated,
	);

	const subunitName = useSelector(
		(state) => state.registration.b2b.form.subunitName,
	);

	const businessName = useSelector(
		(state) => state.registration.b2b.form.businessName,
	);
	const companyAddress = useSelector(
		(state) => state.registration.b2b.form.companyAddress,
	);
	const postcode = useSelector((state) => state.registration.b2b.form.postcode);
	const city = useSelector((state) => state.registration.b2b.form.city);

	const name = useSelector((state) => state.registration.b2b.form.name);
	const phone = useSelector((state) => state.registration.b2b.form.phone);
	const email = useSelector((state) => state.registration.b2b.form.email);
	const password = useSelector((state) => state.registration.b2b.form.password);
	const invoiceEmail = useSelector(
		(state) => state.registration.b2b.form.invoiceEmail,
	);
	const acceptSalesConditions = useSelector(
		(state) => state.registration.b2b.form.acceptSalesConditions,
	);

	const acceptSmsNotifications = useSelector(
		(state) => state.registration.b2b.form.acceptSmsNotifications,
	);

	const acceptOffersInformations = useSelector(
		(state) => state.registration.b2b.form.acceptOffersInformations,
	);

	const acceptMarketingConsents = useSelector(
		(state) => state.registration.b2b.form.acceptMarketingConsents,
	);

	const accept3rdPartyMarketingConsents = useSelector(
		(state) => state.registration.b2b.form.accept3rdPartyMarketingConsents,
	);

	const acceptClubAgreementConsents = useSelector(
		(state) => state.registration.b2b.form.acceptClubAgreementConsents,
	);

	const organisationNumberValidated = useSelector(
		(state) => state.registration.b2b.organisationNumberValidated,
	);

	const isRegisteringNewUser = useSelector(
		(state) => state.registration.isRegisteringNewUser,
	);

	const isCheckingUsername = useSelector(
		(state) => state.registration.b2b.isCheckingUsername,
	);

	const savedUsername = useSelector(
		(state) => state.registration.b2b.savedUsername,
	);

	const alerts = useSelector((state) => state.registration.alerts);
	const isBusinessConsentsEnabled = useSelector(
		(state) => state.settings.features.businessConsents,
	);
	const isCrmMode = useSelector((state) => state.settings.isCrm);
	const country = useSelector((state) => state.settings.country);
	const storeId = useSelector((state) => state.settings.storeId);

	const returnUrl = useSelector(
		(state) => state.registration.b2b.form.returnUrl,
	);
	const validationRules = useSelector(
		(state) => state.settings.validationRules,
	);
	const isEditCompanyDataMode = useSelector(
		(state) => state.registration.b2b.isEditCompanyDataMode,
	);

	const isPublic = useSelector((state) => state.registration.b2b.form.isPublic);

	const lookupId = useSelector((state) => state.registration.b2b.form.lookupId);

	const isCrossCustomerStore = storeId === STORES_ID["321"];
	const isSubunitSearchActive = useSelector(
		(state) => state.registration.b2b.isSubunitSearchActive,
	);

	const missingSubunitData = useSelector(
		(state) => state.registration.b2b.missingSubunitData,
	);

	const useGovernmentSearch = useSelector(
		(state) => state.settings.features.useGovernmentSearch,
	);

	const enableClubAgreementConsent = useSelector(
		(state) => state.settings.features.enableClubAgreementConsent,
	);

	const showMoreFields =
		useGovernmentSearch && isSubunitSearchActive
			? !!(
					organisationNumberValidated &&
					businessName &&
					(subunitOrganisationNumberValidated || missingSubunitData)
				)
			: !!(organisationNumberValidated && businessName);

	const schema = createRegistrationFormSchema({
		dispatch,
		validationRules: {
			...validationRules,
			enableClubAgreementConsent,
			notSameAsUsername: (value) =>
				!isCheckingUsername && savedUsername !== value,
		},
	});

	const formMethods = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
		resolver: yupResolver(schema),
		defaultValues: {
			organisationNumber,
			businessName,
			companyAddress,
			postcode,
			city,
			name,
			phone,
			email,
			invoiceEmail,
			password,
			acceptSalesConditions,
			acceptSmsNotifications,
			accept3rdPartyMarketingConsents,
			acceptMarketingConsents,
			acceptOffersInformations,
			acceptClubAgreementConsents,
		},
	});

	const {
		handleSubmit,
		clearErrors,
		setValue,
		trigger,
		reset,
		formState: { errors },
	} = formMethods;

	const handleResetState = () => {
		dispatch(resetLookupData());
		dispatch(onAlertClose([]));
		reset();
	};

	const handleDebounedFieldChange = useDebouncedCallback(async (payload) => {
		const isValid = await trigger(payload.field);

		if (isValid) {
			clearErrors(payload.field);
		}

		dispatch(handleFieldValueChange(payload));
	}, TYPE_DEBOUNCE);

	const onFieldValueChange = async (event) => {
		const { id, value } = event.target;

		setValue(id, value);

		if (event.isTrusted) {
			const isValid = await trigger(id);

			if (isValid) {
				clearErrors(value);
			}

			dispatch(
				handleFieldValueChange({
					form: "b2b",
					field: id,
					value,
				}),
			);
		} else {
			await handleDebounedFieldChange({
				form: "b2b",
				field: id,
				value,
			});
		}
	};

	const handleRegister = async (data: RegisterBusinessFields) => {
		dispatch(onAlertClose([]));
		dispatch(resetEmailLookup());

		await trigger(["email"]);

		let payload: RegisterBusinessPayload = {
			name: normalizeValue(data.name),
			email: normalizeValue(data.email),
			invoiceEmail: normalizeValue(data.invoiceEmail),
			password: normalizeValue(data.password),
			mobileNumber: normalizeValue(data.phone),
			organisationNumber: normalizeValue(data.organisationNumber),
			subunitName: normalizeValue(data.subunitName),
			subunitOrganisationNumber: normalizeValue(data.subunitOrganisationNumber),
			businessName: normalizeValue(data.businessName),
			lookupId,
			returnUrl,
			country,
			verify: data.verify,
			isPublic,
			acceptSalesConditions: data.acceptSalesConditions,
			address: {
				streetLine1: companyAddress,
				postalCode: postcode,
				city,
				country,
			},
		};

		let validationFields: RegisterUserFormFields = [
			"organisationNumber",
			"password",
			"name",
			"phone",
			"email",
			"invoiceEmail",
			"businessName",
			"companyAddress",
			"postcode",
			"city",
			"acceptSalesConditions",
		];

		if (isBusinessConsentsEnabled && enableClubAgreementConsent) {
			Object.assign(payload, {
				consents: {
					clubAgreement: {
						consented: data.acceptClubAgreementConsents,
						consentText: formatMessage({
							id: "consents.clubAgreement.text",
						}),
					},
					clubSms: {
						consented: data.acceptSmsNotifications,
						consentText: formatMessage({
							id: "consents.clubSms.text",
						}),
					},
				},
			});

			validationFields.push(
				"acceptClubAgreementConsents",
				"acceptSmsNotifications",
			);
		} else {
			Object.assign(payload, {
				consents: {
					newsletterEmail: {
						consented: data.acceptOffersInformations,
						consentText: formatMessage({
							id: "consents.newsletter.text",
						}),
					},
				},
			});
			validationFields.push("acceptOffersInformations");
		}

		const isValid = await trigger(validationFields);

		if (isValid) {
			dispatch(registerBusiness(payload));
		} else {
			Object.entries(errors).forEach(([field]) => {
				trackEvent({
					event: TRACKING_EVENTS.REGISTRATION.EVENT.VALIDATION_ERROR,
					target: TRACKING_EVENTS.REGISTRATION.APP,
					value: field,
				});
			});
		}
	};

	return (
		<>
			<AlertContainer
				alerts={alerts}
				onAlertClose={(data) => dispatch(onAlertClose(data))}
			/>

			<div
				className={clsx({
					"form-wrapper": true,
					extended: showMoreFields,
					club: isBusinessConsentsEnabled,
				})}
			>
				{showMoreFields && isBusinessConsentsEnabled && (
					<ClubBenefits target="sidebar" />
				)}
				<section key="registration" className="form-section">
					<form
						action=""
						autoComplete="off"
						onSubmit={handleSubmit((data) =>
							handleRegister({
								...data,
								verify: true,
							} as RegisterBusinessFields),
						)}
					>
						<AutocompleteFix />
						<fieldset>
							<h1>{formatMessage({ id: "komid.register.wizard.welcome" })}</h1>
							<h3>
								{formatMessage({
									id: "komid.register.wizard.already.have.account",
								})}{" "}
								<a
									href={`/login/local?returnUrl=${encodeURIComponent(returnUrl)}`}
								>
									{formatMessage({ id: "komid.login.form.header" })}
								</a>
							</h3>
							{showMoreFields && (
								<a
									className="back-to-search"
									href="#"
									onClick={handleResetState}
									data-testid="register.business.form.back.link"
								>
									{svgIcon.arrowLeftSmall}{" "}
									{formatMessage({ id: "komid.register.go.back.to.search" })}
								</a>
							)}

							<FormProvider {...formMethods}>
								<CompanySearch
									onChange={onFieldValueChange}
									isFormOpen={showMoreFields}
								/>
								{showMoreFields && (
									<>
										{(isEditCompanyDataMode || isCrmMode) && (
											<CompanyAddress onChange={onFieldValueChange} />
										)}
										<CompanyContact onChange={onFieldValueChange} />
										<Alert
											level="info"
											data-testid="register.business.form.credit.check.information"
											messageKey="komid.register.credit.check.information"
										/>
										<CompanyConsents />

										<div className="form-buttons">
											<SubmitButton
												isFetching={isRegisteringNewUser}
												onClick={handleSubmit(
													(data) =>
														handleRegister({
															...data,
															verify: true,
														} as RegisterBusinessFields),
													(errors) => {
														console.log(errors);
													},
												)}
											>
												{formatMessage({
													id: "komid.register.wizard.register",
												})}
												{isRegisteringNewUser && (
													<SpinningCircle className="loader" />
												)}
											</SubmitButton>

											<div className="form-bottom-links">
												<span>
													{formatMessage({ id: "komid.login.separatortext" })}
												</span>
												<a
													href="#"
													className="link"
													data-testid="register.business.form.skip.verification.link"
													onClick={handleSubmit(
														(data) =>
															handleRegister({
																...data,
																verify: false,
															} as RegisterBusinessFields),
														(errors) => {
															console.log(errors);
														},
													)}
												>
													{formatMessage({
														id: "komid.register.skip.verification",
													})}
												</a>
											</div>
										</div>
									</>
								)}
							</FormProvider>

							{isCrossCustomerStore && (
								<div className="form-bottom-links">
									<p>
										{formatMessage({
											id: "komid.register.form.private.customer",
										})}
									</p>
									<a
										data-testid="register.business.form.register.private.user.link"
										href={`/register/private?returnUrl=${encodeURIComponent(
											returnUrl,
										)}`}
									>
										{formatMessage({
											id: "komid.register.form.private.customer.register.link",
										})}
									</a>
								</div>
							)}
						</fieldset>
					</form>
				</section>
				{showMoreFields && isBusinessConsentsEnabled && (
					<div className="club-spacer" />
				)}
			</div>
		</>
	);
};
